/* eslint-disable no-unused-vars */
import { useCallback, useEffect, useMemo } from 'react';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
    ReactFlow,
    useNodesState,
    useEdgesState,
    Background,
    Controls,
    useReactFlow
} from '@xyflow/react';
import CustomNode from './customNode';
import { generateVisibleFlow, getTreeWidth } from '../utils/treeHelper';

const ManagerialTreeFlow = ({ onNodeClickHandler, tree, setTree, treeRef }) => {
    const theme = useTheme();
    const [nodes, setNodes, onNodesChange] = useNodesState([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState([]);

    const { fitView } = useReactFlow();

    const rebuildLayout = useCallback(
        (updatedTree) => {
            let allNodes = [];
            let allEdges = [];

            // console.log(updatedTree, 'Updated');

            const spacingBetweenTrees = 300;
            const spacingY = 300;

            const expandableRoots = updatedTree.filter(
                (root) => root.subordinates && root.subordinates.length > 0
            );
            const standaloneRoots = updatedTree.filter(
                (root) => !root.subordinates || root.subordinates.length === 0
            );

            // 1. Layout trees with subordinates (horizontal)
            let cumulativeOffsetX = 0;
            expandableRoots.forEach((rootNode) => {
                rootNode.expanded = true;
                const { nodes, edges } = generateVisibleFlow(
                    rootNode,
                    handleToggleExpand,
                    onNodeClickHandler,
                    {
                        spacingX: spacingBetweenTrees,
                        spacingY,
                        offsetX: cumulativeOffsetX
                    }
                );

                const treeWidth = getTreeWidth(nodes);
                cumulativeOffsetX += treeWidth + spacingBetweenTrees;

                allNodes = [...allNodes, ...nodes];
                allEdges = [...allEdges, ...edges];
            });

            // 2. Layout standalone root nodes (vertical stack on right)
            const standaloneStartX = cumulativeOffsetX + spacingBetweenTrees;
            standaloneRoots.forEach((node, index) => {
                allNodes.push({
                    id: node.userId,
                    data: {
                        ...node,
                        onToggleExpand: handleToggleExpand,
                        onNodeClickHandler
                    },
                    position: {
                        x: standaloneStartX,
                        y: index * spacingY
                    },
                    type: 'customNode'
                });
            });

            setNodes(allNodes);
            setEdges(allEdges);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [setNodes, setEdges]
    );

    const handleToggleExpand = useCallback(
        (id) => {
            const toggleExpandImmutable = (node) => {
                if (node.userId === id) {
                    return {
                        ...node,
                        expanded: !node.expanded
                    };
                }

                const updatedSubordinates =
                    node.subordinates?.map(toggleExpandImmutable) || [];
                return {
                    ...node,
                    subordinates: updatedSubordinates
                };
            };

            const updatedTree = treeRef.current.map(toggleExpandImmutable);
            treeRef.current = updatedTree;
            setTree(updatedTree);
            rebuildLayout(updatedTree);
        },
        [rebuildLayout, setTree, treeRef]
    );

    useEffect(() => {
        if (tree.length > 0) {
            rebuildLayout(tree);
        }
    }, [tree, rebuildLayout]);

    const handleNodeClick = useCallback(
        (_, node) => {
            fitView({
                nodes: [{ id: node.id }],
                duration: 300,
                maxZoom: 1,
                minZoom: 0.1
            });
        },
        [fitView]
    );

    const nodeTypes = useMemo(() => ({ customNode: CustomNode }), []);

    return (
        // Use Custom code component is usecallback or something efficient
        <Box sx={{ width: '100%', height: '78vh', bgcolor: 'background.default' }}>
            <ReactFlow
                nodes={nodes}
                edges={edges}
                nodeTypes={nodeTypes}
                // onNodesChange={onNodesChange}
                // onEdgesChange={onEdgesChange}
                onNodeClick={handleNodeClick}
                fitView
                minZoom={0.05}
                panOnScroll
                panOnScrollMode='free'
                zoomOnScroll
                zoomOnScrollMode='zoom'
                zoomOnScrollSpeed={3}
                panOnDrag={[0, 2]} // left or right mouse button
                zoomOnPinch
                proOptions={{ hideAttribution: true }}
            >
                <Background variant='dots' gap={12} size={1} />
                <Controls
                    position='bottom-right'
                    className='rf-controls'
                    style={{
                        background: theme.palette.background.paper,
                        borderRadius: theme.shape.borderRadius,
                        border: `1px solid ${theme.palette.divider}`,
                        boxShadow: theme.shadows[2]
                    }}
                />
                <style>{`
                    .rf-controls .react-flow__controls-button {
                        background: ${theme.palette.background.paper};
                        border-bottom: 1px solid ${theme.palette.divider};
                        fill: ${theme.palette.text.primary};
                        color: ${theme.palette.text.primary};
                    }
                    .rf-controls .react-flow__controls-button:hover {
                        background: ${theme.palette.action.hover};
                    }
                    .rf-controls .react-flow__controls-button svg {
                        fill: ${theme.palette.text.primary};
                        stroke: ${theme.palette.text.primary};
                    }
                `}</style>
                {/* <MiniMap nodeStrokeWidth={3} pannable /> */}
            </ReactFlow>
        </Box>
    );
};

export default ManagerialTreeFlow;
